<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type {
  InitialData as AddressBookAddressDialogFormInitialData,
  ValidData as AddressBookAddressDialogFormValidData,
} from '@/components/dialogForms/AddressBookAddressDialogForm.vue';

import { useTemplateRef } from 'vue';

import KBtn from '@/components/btns/KBtn.vue';
import AddressBookAddressDialogForm from '@/components/dialogForms/AddressBookAddressDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isEdit?: boolean;
  isConfirmLoading?: boolean;
  initialData?: AddressBookAddressDialogFormInitialData;
  confirm?: (
    formData: AddressBookAddressDialogFormValidData,
  ) => Promise<unknown>;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isEdit: false,
  isConfirmLoading: false,
  initialData: () => ({}),
  confirm: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: AddressBookAddressDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: AddressBookAddressDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}
</script>

<template>
  <PageDialogWrapper
    :class="$style.addressBookAddressDialog"
    data-testid="addressBookAddressDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        {{ props.isEdit ? 'Edit address' : 'Add address' }}
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <AddressBookAddressDialogForm
        :initial-data="props.initialData"
        @submit="handleSubmit"
      />
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList" class="!-mt-6">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-check"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            :disabled="!props.confirm"
            @click="handleConfirmClick"
          >
            {{ props.isEdit ? 'Save address' : 'Add address' }}
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.addressBookAddressDialog {
  width: 420px;
}
  .main {
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
